import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Link, useNavigate, useSearchParams} from 'react-router-dom'
import {ExclamationTriangleIcon} from '@heroicons/react/24/outline'
import {BackendApi} from '../../../../../api/api.ts'
import {User} from '../../../../../api/accessControl/User.ts'
import Shell from '../../../../../components/specific/Shell.tsx'
import CustomerFormModal from '../../../../../components/specific/CustomerFormModal.tsx'
import type {SalesInvoiceDetail} from '../../components/salesInvoices/types/salesInvoice.ts'
import {draftToFormState} from './utils/draftMapping.ts'
import Modal from '../../../../../components/general/Modal.tsx'
import {AddressFieldValue} from '../../../../../components/general/AddressField.tsx'
import {
    CustomerBankAccount,
    CustomerBankAccountWithIban,
    filterBankAccountsThatCanReceivePayments,
} from '../../../../../api/customerBankAccounts/CustomerBankAccount.ts'
import {CustomerBankAccountRepository} from '../../../../../api/customerBankAccounts/CustomerBankAccountRepository.ts'
import {Customer, CustomerDraft} from '../../../../../api/customers/Customer.ts'
import {useCustomers} from '../../../../../hooks/useCustomers.ts'
import {
    mapVATCategoryToDropdownVAT,
} from '../../../../../types/enums.ts'
import {useNotifications} from '../../../../../lib/NotificationContext.tsx'
import {decimalToLocalizedString, isDecimalInSafeRange, MAX_SAFE_DECIMAL} from '../../../../../lib/Money.ts'
import {extractApiErrorMessage} from '../../../../../lib/apiErrors.ts'
import {downloadBlob} from '../../../../../utils/base64UrlConverters.ts'
import {CatalogItem} from '../../../../../types/catalogue.ts'
import {listCatalogItems} from '../CatalogPage/catalogApi.ts'
import {EMPTY_ADDRESS, InvoiceCustomer, InvoiceLineItem, paymentTermOptions} from './types.ts'
import {
    computeInvoiceTotals,
    isLineFilled,
    resolveInvoiceNumberPreview,
    yearFromISODate,
} from './utils/invoiceLines.ts'
import {
    changeSalesInvoiceDraft,
    createSalesInvoiceDraft,
    downloadSalesInvoicePdf,
    getSalesInvoiceNumbering,
    InvoiceFile,
    InvoiceFormState,
    issueSalesInvoice,
    previewSalesInvoice,
    SalesInvoiceNumbering,
} from './salesInvoiceApi.ts'
import {AiPromptCard} from './components/AiPromptCard.tsx'
import {CustomerSection} from './components/CustomerSection.tsx'
import {LineItemsSection, newEmptyLine} from './components/LineItemsSection.tsx'
import {InvoiceDetailsSection} from './components/InvoiceDetailsSection.tsx'
import {InvoiceNumberSection} from './components/InvoiceNumberSection.tsx'
import {ReferenceSection} from './components/ReferenceSection.tsx'
import {RecurringInvoiceCard} from './components/RecurringInvoiceCard.tsx'
import {createRecurringSeries} from '../RecurringInvoicesPage/recurringInvoicesApi.ts'
import {formatDateNL} from '../RecurringInvoicesPage/utils/recurringInvoiceFormat.ts'
import {shouldPrefillCustomer} from './utils/prefillCustomer.ts'
import {buildRecurringSeriesRequest} from './utils/recurringSeriesFromForm.ts'
import {InvoiceActionsBar} from './components/InvoiceActionsBar.tsx'
import InvoicePreview, {InvoicePreviewSeller} from './InvoicePreview.tsx'
import {
    SendInvoiceEmailModal,
    type SendInvoiceEmailValues,
} from '../../components/salesInvoices/SendInvoiceEmailModal.tsx'
import {
    buildDefaultInvoiceEmailBody,
    buildInvoiceEmailIntro,
    type InvoiceEmailMessageContext,
} from '../../components/salesInvoices/utils/invoiceEmailMessage.ts'
import type {PaymentTerm, RecurringCadence, VATCategory} from '../../../../../generated/enums.ts';

const FOOTER_TEXT_SETTING = 'SALES_INVOICE_FOOTER_TEXT'
const DEFAULT_PAYMENT_TERM_SETTING = 'SALES_INVOICE_DEFAULT_PAYMENT_TERM'
const DEFAULT_EMAIL_MESSAGE_SETTING = 'SALES_INVOICE_DEFAULT_EMAIL_MESSAGE'
const INVOICE_CONTACT_EMAIL_SETTING = 'SALES_INVOICE_CONTACT_EMAIL'

type FieldErrorKey = 'customer' | 'address' | 'invoiceDate' | 'lines' | 'bankAccount'
type FieldErrors = Partial<Record<FieldErrorKey, string>>

function todayISO(): string {
    const d = new Date()
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${yyyy}-${mm}-${dd}`
}

function isValidISODate(iso: string): boolean {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false
    const [year, month, day] = iso.split('-').map(Number)
    const d = new Date(year, month - 1, day)
    return d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day
}

function isPaymentTerm(value: unknown): value is PaymentTerm {
    return paymentTermOptions.some((option) => option.value === value)
}

function toInvoiceCustomer(customer: Customer): InvoiceCustomer {
    return {
        id: customer.id,
        name: customer.company_name,
        email: customer.email ?? '',
        ccEmail: customer.fixed_cc_email ?? '',
        vatNumber: customer.vat_number ?? '',
        addressExtraLine: customer.address_extra_line ?? '',
        address: {
            street_name: customer.address.street ?? '',
            house_number: customer.address.house_number ?? '',
            city_name: customer.address.city ?? '',
            postal_code: customer.address.postal_code ?? '',
            country: customer.address.country || 'NL',
        },
        defaultPaymentTerm: isPaymentTerm(customer.default_payment_term_days)
            ? customer.default_payment_term_days
            : null,
    }
}

export default function CreateInvoicePage({currentUser}: { currentUser: User }) {
    const api = useMemo(() => new BackendApi(currentUser.getToken()), [currentUser])
    const navigate = useNavigate()
    const {dispatch: dispatchNotification} = useNotifications()
    const {
        customers: savedCustomers,
        isLoading: isLoadingCustomers,
        createCustomer,
        changeCustomer,
    } = useCustomers(currentUser)

    const [selectedCustomer, setSelectedCustomer] = useState<InvoiceCustomer | null>(null)
    const [address, setAddress] = useState<AddressFieldValue>({...EMPTY_ADDRESS})
    const [isNewCustomerModalOpen, setIsNewCustomerModalOpen] = useState(false)
    const [customerModalSession, setCustomerModalSession] = useState(0)
    const [isSavingCustomer, setIsSavingCustomer] = useState(false)

    const [catalogItems, setCatalogItems] = useState<CatalogItem[]>([])
    const [lines, setLines] = useState<InvoiceLineItem[]>([newEmptyLine()])
    const [invoiceDate, setInvoiceDate] = useState(todayISO())
    const [paymentTerm, setPaymentTerm] = useState<PaymentTerm>(30)
    const [fiscalYear, setFiscalYear] = useState<number | null>(null)
    const [poNumber, setPoNumber] = useState('')
    const [customerReference, setCustomerReference] = useState('')
    const [footerText, setFooterText] = useState('')
    const [defaultFooterText, setDefaultFooterText] = useState('')
    const [isSavingFooterText, setIsSavingFooterText] = useState(false)
    const [numberPrefix, setNumberPrefix] = useState('')
    const [isRecurring, setIsRecurring] = useState(false)
    const [recurringCadence, setRecurringCadence] = useState<RecurringCadence>('monthly')

    const [bankAccounts, setBankAccounts] = useState<CustomerBankAccountWithIban[]>([])
    const [selectedBankAccount, setSelectedBankAccount] = useState<CustomerBankAccountWithIban | null>(null)

    const [searchParams] = useSearchParams()
    const editingDraftId = searchParams.get('draft')
    const prefillCustomerId = searchParams.get('customer')
    const hasPrefilledCustomer = useRef(false)
    const [draftId, setDraftId] = useState<string | null>(editingDraftId)
    const [draftSavedLabel, setDraftSavedLabel] = useState<string | undefined>(undefined)
    const [numbering, setNumbering] = useState<SalesInvoiceNumbering | null>(null)
    const [isLoadingNumbering, setIsLoadingNumbering] = useState(true)

    const [errors, setErrors] = useState<FieldErrors>({})
    const [aiPrompt, setAiPrompt] = useState('')
    const [isGenerating, setIsGenerating] = useState(false)
    const [isCreating, setIsCreating] = useState(false)
    const [isSavingDraft, setIsSavingDraft] = useState(false)
    const [isPreviewing, setIsPreviewing] = useState(false)
    const [isSendModalOpen, setIsSendModalOpen] = useState(false)
    const [isSendingEmail, setIsSendingEmail] = useState(false)
    const [storedEmailMessage, setStoredEmailMessage] = useState('')
    const [business, setBusiness] = useState<{
        name?: string | null,
        website?: string | null,
        registration_number?: string | null,
        vat_number?: string | null,
    } | null>(null)
    const [logoDataUri, setLogoDataUri] = useState<string | null>(null)
    const [isPreviewSheetOpen, setIsPreviewSheetOpen] = useState(false)

    const invoiceContactEmail = settingString(INVOICE_CONTACT_EMAIL_SETTING) || currentUser.email

    const customers = useMemo(() => savedCustomers.map(toInvoiceCustomer), [savedCustomers])
    const totals = useMemo(() => computeInvoiceTotals(lines), [lines])
    const invoiceYear = yearFromISODate(invoiceDate)

    async function load() {
        try {
            const accounts = filterBankAccountsThatCanReceivePayments(
                await new CustomerBankAccountRepository(api).getMyBankAccounts(),
            )
            setBankAccounts(accounts)
            if (accounts.length > 0) {
                setSelectedBankAccount(accounts[0])
            }

            setCatalogItems(await listCatalogItems(api))

            await currentUser.businessSettings.refresh()
            applySettingDefaults()
            setBusiness(await api.Business__myBusiness({}))
            await loadLogo()

            await loadDraftIntoForm(accounts)
        } catch (error) {
            console.error('Error loading invoice page data:', error)
            dispatchNotification({
                title: 'Fout bij laden',
                description: 'Het laden van klanten of bankrekeningen is mislukt. Herlaad de pagina om het opnieuw te proberen.',
                type: 'error',
            })
        }
    }

    async function loadDraftIntoForm(accounts: CustomerBankAccountWithIban[]) {
        if (!editingDraftId) return

        const detail = await api.Me__business__getSalesInvoice(editingDraftId) as SalesInvoiceDetail
        if (detail.display_status !== 'draft') {
            dispatchNotification({
                title: 'Factuur is al definitief',
                description: 'Deze factuur heeft al een nummer en kan niet meer worden bewerkt.',
                type: 'error',
            })
            navigate(`/invoices/${editingDraftId}`)
            return
        }

        const form = draftToFormState(detail)
        setSelectedCustomer(form.customer)
        setAddress(form.address)
        setLines(form.lines.length > 0 ? form.lines : [newEmptyLine()])
        setInvoiceDate(form.invoiceDate)
        setPaymentTerm(form.paymentTerm)
        setPoNumber(form.poNumber)
        setCustomerReference(form.customerReference)
        setFiscalYear(form.fiscalYear)
        setFooterText(form.footerText)

        const matchingAccount = accounts.find(account => account.iban === detail.bank_account_iban)
        if (matchingAccount) {
            setSelectedBankAccount(matchingAccount)
        }
    }

    async function loadLogo() {
        try {
            const logo = await api.Me__business__logo({})
            setLogoDataUri(`data:${logo.content_type};base64,${logo.file_data}`)
        } catch {
            setLogoDataUri(null)
        }
    }

    function readSetting(key: string): unknown {
        try {
            return currentUser.businessSettings.getSetting(key)
        } catch {
            return undefined
        }
    }

    function settingString(key: string): string {
        const value = readSetting(key)
        return typeof value === 'string' ? value : ''
    }

    function applySettingDefaults() {
        const storedFooter = readSetting(FOOTER_TEXT_SETTING)
        if (typeof storedFooter === 'string') {
            setDefaultFooterText(storedFooter)
            setFooterText(storedFooter)
        }

        const storedTerm = Number(readSetting(DEFAULT_PAYMENT_TERM_SETTING))
        if (isPaymentTerm(storedTerm)) {
            setPaymentTerm(storedTerm)
        }

        setStoredEmailMessage(settingString(DEFAULT_EMAIL_MESSAGE_SETTING))
    }

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- async data load, same pattern as the rest of the app
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser])

    useEffect(() => {
        if (!shouldPrefillCustomer({
            hasPrefilled: hasPrefilledCustomer.current,
            prefillCustomerId,
            editingDraftId,
            isLoadingCustomers,
        })) return

        const match = customers.find(candidate => candidate.id === prefillCustomerId)
        if (match) handleSelectCustomer(match)
        hasPrefilledCustomer.current = true
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customers, isLoadingCustomers, prefillCustomerId, editingDraftId])

    const refreshNumbering = useCallback(async (year: number, prefix: string) => {
        setIsLoadingNumbering(true)
        try {
            setNumbering(await getSalesInvoiceNumbering(api, year, prefix.trim() || null))
        } catch (error) {
            console.error('Error loading invoice numbering:', error)
            setNumbering(null)
        } finally {
            setIsLoadingNumbering(false)
        }
    }, [api])

    useEffect(() => {
        const handle = setTimeout(() => {
            refreshNumbering(invoiceYear, numberPrefix)
        }, 400)
        return () => clearTimeout(handle)
    }, [invoiceYear, numberPrefix, refreshNumbering])

    function clearError(key: FieldErrorKey) {
        setErrors(prev => ({...prev, [key]: undefined}))
    }

    function markDraftDirty() {
        setDraftSavedLabel(undefined)
    }

    function hasSetting(key: string): boolean {
        const value = readSetting(key)
        return value !== undefined && value !== null && value !== ''
    }

    function getMissingSettings(): Array<{ label: string, key: string }> {
        const missing: Array<{ label: string, key: string }> = []
        if (!business) return missing

        const hasAddress = hasSetting('BUSINESS_ADDRESS_STREET_NAME') &&
            hasSetting('BUSINESS_ADDRESS_HOUSE_NUMBER') &&
            hasSetting('BUSINESS_ADDRESS_CITY_NAME') &&
            hasSetting('BUSINESS_ADDRESS_POSTAL_CODE')

        if (!hasAddress) missing.push({label: 'Bedrijfsadres', key: 'address'})
        if (!business?.registration_number) missing.push({label: 'KvK nummer', key: 'kvk'})
        if (!business?.vat_number) missing.push({label: 'BTW nummer', key: 'vat_number'})
        return missing
    }

    const missingSettings = business ? getMissingSettings() : []
    const hasAllRequiredSettings = missingSettings.length === 0

    function handleSelectCustomer(customer: InvoiceCustomer | null) {
        const isDifferentCustomer = customer?.id !== selectedCustomer?.id
        setSelectedCustomer(customer)
        markDraftDirty()
        if (!customer) return

        clearError('customer')
        const knownAddress = customer.address
        if (knownAddress && (knownAddress.street_name || knownAddress.city_name)) {
            setAddress(knownAddress)
            clearError('address')
        } else if (isDifferentCustomer) {
            setAddress({...EMPTY_ADDRESS})
        }

        if (customer.defaultPaymentTerm !== null) {
            setPaymentTerm(customer.defaultPaymentTerm)
        }
    }

    async function handleCreateCustomer(draft: CustomerDraft) {
        setIsSavingCustomer(true)
        try {
            const created = await createCustomer(draft)
            setIsNewCustomerModalOpen(false)
            handleSelectCustomer(toInvoiceCustomer(created))
            dispatchNotification({
                title: 'Klant toegevoegd',
                description: `${created.company_name} staat nu in je klantenlijst.`,
                type: 'success',
            })
        } catch (error) {
            console.error('Error creating customer:', error)
            dispatchNotification({
                title: 'Fout bij opslaan',
                description: extractApiErrorMessage(error) ?? 'De klant kon niet worden opgeslagen.',
                type: 'error',
            })
        } finally {
            setIsSavingCustomer(false)
        }
    }

    function handleBankAccountChange(account: CustomerBankAccount | null) {
        if (!account?.canReceivePayments()) return
        setSelectedBankAccount(account as CustomerBankAccountWithIban)
        clearError('bankAccount')
        markDraftDirty()
    }

    function validate(): { valid: boolean, errs: FieldErrors } {
        const errs: FieldErrors = {}

        if (!selectedCustomer || !selectedCustomer.name.trim()) {
            errs.customer = 'Kies een klant of voeg een nieuwe toe.'
        }

        if (!address.street_name?.trim()) errs.address = 'Straatnaam is verplicht.'
        else if (!address.house_number?.trim()) errs.address = 'Huisnummer is verplicht.'
        else if (!address.city_name?.trim()) errs.address = 'Plaats is verplicht.'
        else if (!address.postal_code?.trim()) errs.address = 'Postcode is verplicht.'
        else if (!address.country?.trim()) errs.address = 'Land is verplicht.'

        if (!invoiceDate || !isValidISODate(invoiceDate)) {
            errs.invoiceDate = 'Factuurdatum is verplicht en moet een geldige datum zijn.'
        }

        const filledLines = lines.filter(isLineFilled)
        if (filledLines.length === 0) {
            errs.lines = 'Voeg minstens één factuurregel toe.'
        } else if (filledLines.some(line => !line.description.trim())) {
            errs.lines = 'Vul bij elke regel een omschrijving in.'
        } else if (filledLines.some(line => line.unitPrice === null)) {
            errs.lines = 'Vul bij elke regel een stukprijs in.'
        } else if (filledLines.some(line => line.quantity === null || line.quantity <= 0)) {
            errs.lines = 'Vul bij elke regel een aantal groter dan 0 in.'
        } else if (totals.totalExclVat < 0) {
            errs.lines = 'Het factuurtotaal mag niet negatief zijn. Verlaag de korting.'
        } else if (!isDecimalInSafeRange(totals.totalExclVat)) {
            errs.lines = `Het factuurtotaal is te groot (max. € ${MAX_SAFE_DECIMAL}).`
        }

        if (!selectedBankAccount) {
            errs.bankAccount = 'Selecteer een bankrekening.'
        }

        return {valid: Object.keys(errs).length === 0, errs}
    }

    function focusFirstError(errs: FieldErrors) {
        const fieldOrder: Array<{ key: FieldErrorKey, elementId: string }> = [
            {key: 'customer', elementId: 'customer-section'},
            {key: 'address', elementId: 'address-container'},
            {key: 'lines', elementId: 'lines-section'},
            {key: 'invoiceDate', elementId: 'invoiceDate-input'},
            {key: 'bankAccount', elementId: 'bank-account-selector'},
        ]
        const first = fieldOrder.find(({key}) => errs[key])
        if (!first) return
        const el = document.getElementById(first.elementId)
        if (el) {
            el.scrollIntoView({behavior: 'smooth', block: 'center'})
            el.focus()
        }
    }

    function validateOrFocus(): boolean {
        const {valid, errs} = validate()
        setErrors(errs)
        if (!valid) focusFirstError(errs)
        return valid
    }

    function currentForm(): InvoiceFormState {
        return {
            customer: selectedCustomer,
            address,
            lines,
            invoiceDate,
            paymentTerm,
            fiscalYear,
            bankAccountId: selectedBankAccount?.id ?? null,
            poNumber,
            customerReference,
            footerText,
        }
    }

    function reportFailure(title: string, error: unknown, fallback: string) {
        console.error(fallback, error)
        dispatchNotification({
            title,
            description: extractApiErrorMessage(error) ?? fallback,
            type: 'error',
        })
    }

    async function persistDraft(): Promise<string> {
        const form = currentForm()
        const saved = draftId !== null
            ? await changeSalesInvoiceDraft(api, draftId, form)
            : await createSalesInvoiceDraft(api, form)

        setDraftId(saved.id)
        return saved.id
    }

    function recurringDisabledReason(): string | undefined {
        if (!selectedCustomer) {
            return 'Kies eerst een klant om deze factuur te laten terugkeren.'
        }
        if (selectedCustomer.isAdHoc) {
            return 'Sla deze klant eerst op als klant om de factuur te laten terugkeren.'
        }
        if (!selectedBankAccount) {
            return 'Selecteer eerst een bankrekening om de factuur te laten terugkeren.'
        }
        if (!isValidISODate(invoiceDate)) {
            return 'Vul eerst een geldige factuurdatum in.'
        }
        return undefined
    }

    async function createSeriesForIssuedInvoice(
        customer: InvoiceCustomer | null,
        bankAccountId: string | null,
    ) {
        try {
            const request = buildRecurringSeriesRequest({
                customer,
                bankAccountId,
                lines,
                invoiceDateISO: invoiceDate,
                paymentTerm,
                cadence: recurringCadence,
            })
            const created = await createRecurringSeries(api, request)

            dispatchNotification({
                title: 'Terugkerende factuur ingesteld',
                description: `De volgende factuur voor ${created.customer_name} wordt automatisch `
                    + `aangemaakt en verstuurd op ${formatDateNL(created.next_run_on)}.`,
                type: 'success',
            })
        } catch (error) {
            reportFailure(
                'Reeks niet ingesteld',
                error,
                'De factuur is aangemaakt, maar de terugkerende reeks kon niet worden ingesteld. '
                + 'Probeer het opnieuw via Terugkerende facturen.',
            )
        }
    }

    function resetAfterIssuing() {
        setDraftId(null)
        setDraftSavedLabel(undefined)
        setIsRecurring(false)
        setRecurringCadence('monthly')
        setLines([newEmptyLine()])
        setSelectedCustomer(null)
        setAddress({...EMPTY_ADDRESS})
        setPoNumber('')
        setCustomerReference('')
        setFiscalYear(null)
        setFooterText(defaultFooterText)
        refreshNumbering(invoiceYear, numberPrefix)
    }

    function deliverFile(file: InvoiceFile) {
        downloadBlob(file.blob, file.fileName)
    }

    async function saveDraft() {
        if (!validateOrFocus()) return

        setIsSavingDraft(true)
        try {
            await persistDraft()
            setDraftSavedLabel('Concept opgeslagen')
            dispatchNotification({
                title: 'Concept opgeslagen',
                description: 'De factuur is bewaard als concept. Er is nog geen factuurnummer toegekend.',
                type: 'success',
            })
        } catch (error) {
            reportFailure('Fout bij opslaan', error, 'Het concept kon niet worden opgeslagen.')
        } finally {
            setIsSavingDraft(false)
        }
    }

    async function preview() {
        if (!validateOrFocus()) return

        setIsPreviewing(true)
        try {
            const file = await previewSalesInvoice(api, currentForm())
            const url = URL.createObjectURL(file.blob)
            if (!window.open(url, '_blank')) {
                URL.revokeObjectURL(url)
                deliverFile(file)
            }
        } catch (error) {
            reportFailure('Fout bij voorbeeld', error, 'Het voorbeeld kon niet worden gemaakt.')
        } finally {
            setIsPreviewing(false)
        }
    }

    async function saveAndDownload() {
        if (!validateOrFocus()) return

        setIsCreating(true)
        try {
            const invoiceId = await persistDraft()
            const issued = await issueSalesInvoice(api, invoiceId, numberPrefix.trim() || null)

            if (isRecurring) {
                await createSeriesForIssuedInvoice(selectedCustomer, selectedBankAccount?.id ?? null)
            }

            resetAfterIssuing()
            dispatchNotification({
                title: `Factuur ${issued.invoice_number ?? ''} opgeslagen`.trim(),
                description: 'De factuur is definitief gemaakt en wordt gedownload.',
                type: 'success',
            })

            try {
                deliverFile(await downloadSalesInvoicePdf(api, invoiceId))
            } catch (downloadError) {
                reportFailure(
                    'Downloaden mislukt',
                    downloadError,
                    `Factuur ${issued.invoice_number ?? ''} is aangemaakt, maar de PDF kon niet worden gedownload. Je vindt de factuur terug in je administratie.`.trim(),
                )
            }
        } catch (error) {
            reportFailure('Fout bij genereren', error, 'Er is een fout opgetreden bij het genereren van de factuur.')
        } finally {
            setIsCreating(false)
        }
    }

    function openSendModal() {
        if (!validateOrFocus()) return
        setIsSendModalOpen(true)
    }

    async function issueAndSend(values: SendInvoiceEmailValues) {
        setIsSendingEmail(true)
        const customerToRemember = selectedCustomer
        try {
            const invoiceId = await persistDraft()
            const issued = await issueSalesInvoice(api, invoiceId, numberPrefix.trim() || null)

            if (isRecurring) {
                await createSeriesForIssuedInvoice(customerToRemember, selectedBankAccount?.id ?? null)
            }

            setIsSendModalOpen(false)
            resetAfterIssuing()

            try {
                await api.Me__business__sendSalesInvoice({
                    invoice_id: invoiceId,
                    to_email: values.toEmail,
                    cc_email: values.ccEmail || null,
                    bcc_email: values.bccEmail || null,
                    reply_to_email: values.replyToEmail || null,
                    message: values.message || null,
                })
                await persistSendPreferences(values, customerToRemember)
                dispatchNotification({
                    title: 'Factuur verstuurd',
                    description: `De factuur is per e-mail verstuurd naar ${values.toEmail}.`,
                    type: 'success',
                })
            } catch (sendError) {
                reportFailure(
                    'Factuur aangemaakt, e-mail niet verstuurd',
                    sendError,
                    `Factuur ${issued.invoice_number ?? ''} staat in je facturenoverzicht; verstuur de e-mail daar opnieuw.`.trim(),
                )
            }
        } catch (error) {
            reportFailure('Fout bij genereren', error, 'Er is een fout opgetreden bij het genereren van de factuur.')
        } finally {
            setIsSendingEmail(false)
        }
    }

    async function persistSendPreferences(
        values: SendInvoiceEmailValues,
        customer: InvoiceCustomer | null,
    ) {
        try {
            if (values.rememberMessage) {
                await currentUser.businessSettings.setSetting(
                    DEFAULT_EMAIL_MESSAGE_SETTING, values.messageBody,
                )
                setStoredEmailMessage(values.messageBody)
            }

            if (values.rememberCc && customer && !customer.isAdHoc) {
                const saved = savedCustomers.find(candidate => candidate.id === customer.id)
                if (saved) {
                    await changeCustomer(saved.id, {...saved, fixed_cc_email: values.ccEmail || null})
                }
            }
        } catch (error) {
            reportFailure(
                'Voorkeuren niet bewaard',
                error,
                'De factuur is verstuurd, maar je voorkeuren konden niet worden bewaard.',
            )
        }
    }

    async function saveFooterTextAsDefault() {
        setIsSavingFooterText(true)
        try {
            await currentUser.businessSettings.setSetting(FOOTER_TEXT_SETTING, footerText)
            setDefaultFooterText(footerText)
            dispatchNotification({
                title: 'Standaardtekst bewaard',
                description: 'Deze tekst staat voortaan standaard op je nieuwe facturen.',
                type: 'success',
            })
        } catch (error) {
            reportFailure('Fout bij opslaan', error, 'De standaardtekst kon niet worden opgeslagen.')
        } finally {
            setIsSavingFooterText(false)
        }
    }

    async function generateFromPrompt() {
        if (!aiPrompt.trim()) return

        setIsGenerating(true)
        try {
            const result = await api.Business__invoice__generateFromPrompt({prompt: aiPrompt})

            if (result.relation) {
                const matched = customers.find(c => c.name.toLowerCase() === result.relation?.toLowerCase())
                handleSelectCustomer(matched ?? {
                    id: `__custom__:${result.relation}`,
                    name: result.relation,
                    email: '',
                    ccEmail: '',
                    vatNumber: '',
                    addressExtraLine: '',
                    address: null,
                    defaultPaymentTerm: null,
                    isAdHoc: true,
                })
            } else {
                setSelectedCustomer(null)
            }

            if (result.address_street_name || result.address_house_number || result.address_postal_code || result.address_city || result.address_country_code) {
                setAddress(prev => ({
                    street_name: result.address_street_name || prev.street_name,
                    house_number: result.address_house_number || prev.house_number,
                    city_name: result.address_city || prev.city_name,
                    postal_code: result.address_postal_code || prev.postal_code,
                    country: result.address_country_code || prev.country || 'NL',
                }))
                clearError('address')
            }

            if (result.invoiceDate) {
                setInvoiceDate(result.invoiceDate)
                clearError('invoiceDate')
            }

            if (isPaymentTerm(result.paymentTermDays)) {
                setPaymentTerm(result.paymentTermDays)
            }

            if (result.description || (result.total !== null && result.total !== undefined)) {
                const vat = result.vat
                    ? mapVATCategoryToDropdownVAT(result.vat as VATCategory, result.vat_percentage ?? 21)
                    : undefined
                const total = result.total !== null && result.total !== undefined && isDecimalInSafeRange(result.total)
                    ? result.total
                    : null
                setLines([newEmptyLine({
                    description: result.description ?? '',
                    quantity: 1,
                    unitPrice: total,
                    ...(vat ? {vat} : {}),
                })])
                clearError('lines')
            }

            if (result.seller_iban) {
                const matchedAccount = bankAccounts.find(acc => acc.iban === result.seller_iban)
                if (matchedAccount) {
                    setSelectedBankAccount(matchedAccount)
                    clearError('bankAccount')
                }
            }

            markDraftDirty()
            setAiPrompt('')
            dispatchNotification({
                title: 'Velden ingevuld',
                description: 'De factuurvelden zijn voor je ingevuld. Controleer ze en vul aan waar nodig.',
                type: 'success',
            })
        } catch (error) {
            console.error('Error generating invoice:', error)
            dispatchNotification({
                title: 'Fout bij genereren',
                description: 'Er is een fout opgetreden bij het genereren van de factuur. Probeer het opnieuw.',
                type: 'error',
            })
        } finally {
            setIsGenerating(false)
        }
    }

    function openNewCustomerModal() {
        setCustomerModalSession(session => session + 1)
        setIsNewCustomerModalOpen(true)
    }

    const newCustomerInitialName = selectedCustomer?.isAdHoc ? selectedCustomer.name : ''

    const sendInvoiceNumber = resolveInvoiceNumberPreview(
        invoiceDate, numberPrefix, numbering,
    ).numberPreview

    const emailMessageContext: InvoiceEmailMessageContext = {
        invoiceNumber: sendInvoiceNumber,
        totalAmount: totals.totalInclVat,
        paymentTermDays: paymentTerm,
        customerName: selectedCustomer?.name,
        entrepreneurName: currentUser.getFullName(),
        companyName: business?.name ?? currentUser.getBusinessName(),
    }

    const emailIntro = buildInvoiceEmailIntro(emailMessageContext)
    const defaultEmailBody = storedEmailMessage.trim() || buildDefaultInvoiceEmailBody(emailMessageContext)

    const previewSeller: InvoicePreviewSeller = {
        businessName: business?.name ?? currentUser.getBusinessName() ?? '',
        streetWithHouseNumber: [
            settingString('BUSINESS_ADDRESS_STREET_NAME'),
            settingString('BUSINESS_ADDRESS_HOUSE_NUMBER'),
        ].filter(Boolean).join(' '),
        postalCode: settingString('BUSINESS_ADDRESS_POSTAL_CODE'),
        city: settingString('BUSINESS_ADDRESS_CITY_NAME'),
        website: business?.website ?? null,
        kvkNumber: business?.registration_number ?? null,
        vatNumber: business?.vat_number ?? null,
        contactName: currentUser.getFullName(),
        contactEmail: invoiceContactEmail,
        logoDataUri,
        iban: selectedBankAccount?.iban ?? null,
        ibanOwnerName: selectedBankAccount?.ownerName ?? null,
    }

    const invoicePreview = (
        <InvoicePreview
            seller={previewSeller}
            buyerName={selectedCustomer?.name ?? ''}
            buyerStreetWithHouseNumber={[address.street_name, address.house_number].filter(Boolean).join(' ')}
            buyerPostalCode={address.postal_code}
            buyerCity={address.city_name}
            invoiceNumber={resolveInvoiceNumberPreview(invoiceDate, numberPrefix, numbering).numberPreview}
            invoiceDateISO={invoiceDate}
            paymentTermDays={paymentTerm}
            purchaseOrderReference={poNumber}
            customerSpecificReference={customerReference}
            lines={lines}
            totals={totals}
            footerText={footerText}
        />
    )

    return (
        <Shell
            currentUser={currentUser}
            title={editingDraftId ? "Concept bewerken" : "Factuur aanmaken"}
            subtitle={editingDraftId
                ? "Werk dit concept bij en maak het definitief wanneer je klaar bent."
                : "Vertel wat je wilt factureren — wij maken de factuur."}
        >
            {!hasAllRequiredSettings && (
                <div className="border border-yellow-300 bg-yellow-50 rounded-lg p-4 my-2 mb-6 flex items-start gap-3 max-w-4xl">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5"/>
                    <span className="text-yellow-800">
                        Om facturen te maken, vul je aub nog deze gegevens aan: {missingSettings.map(s => s.label).join(', ')}.
                        Dat kan in <Link to={`/my-profile?tab=1&missingFields=${missingSettings.map(s => s.key).join(',')}`} className="underline">Instellingen</Link>.
                    </span>
                </div>
            )}

            <div className="flex flex-col items-start gap-8 xl:flex-row">
                <div className="w-full min-w-0 max-w-4xl xl:max-w-2xl 2xl:max-w-3xl">
                    <AiPromptCard
                        value={aiPrompt}
                        onChange={setAiPrompt}
                        onSubmit={generateFromPrompt}
                        isGenerating={isGenerating}
                        disabled={!hasAllRequiredSettings}
                    />

                    <form className="mt-10 space-y-10" onSubmit={(e) => e.preventDefault()}>
                        <CustomerSection
                            customers={customers}
                            selectedCustomer={selectedCustomer}
                            onSelectCustomer={handleSelectCustomer}
                            address={address}
                            onAddressChange={(value) => {
                                setAddress(value)
                                clearError('address')
                                markDraftDirty()
                            }}
                            onOpenNewCustomer={openNewCustomerModal}
                            isLoadingCustomers={isLoadingCustomers}
                            customerError={errors.customer}
                            addressError={errors.address}
                        />

                        <LineItemsSection
                            lines={lines}
                            onChange={(value) => {
                                setLines(value)
                                clearError('lines')
                                markDraftDirty()
                            }}
                            catalogItems={catalogItems}
                            linesError={errors.lines}
                        />

                        <InvoiceDetailsSection
                            invoiceDate={invoiceDate}
                            onInvoiceDateChange={(value) => {
                                setInvoiceDate(value)
                                clearError('invoiceDate')
                                markDraftDirty()
                            }}
                            paymentTerm={paymentTerm}
                            onPaymentTermChange={(value) => {
                                setPaymentTerm(value)
                                markDraftDirty()
                            }}
                            bankAccounts={bankAccounts}
                            selectedBankAccount={selectedBankAccount}
                            onSelectBankAccount={handleBankAccountChange}
                            fiscalYear={fiscalYear}
                            onFiscalYearChange={(value) => {
                                setFiscalYear(value)
                                markDraftDirty()
                            }}
                            invoiceDateError={errors.invoiceDate}
                            bankAccountError={errors.bankAccount}
                        />

                        <InvoiceNumberSection
                            invoiceDate={invoiceDate}
                            prefix={numberPrefix}
                            onPrefixChange={setNumberPrefix}
                            numbering={numbering}
                            isLoadingNumbering={isLoadingNumbering}
                        />

                        <ReferenceSection
                            poNumber={poNumber}
                            onPoNumberChange={(value) => {
                                setPoNumber(value)
                                markDraftDirty()
                            }}
                            customerReference={customerReference}
                            onCustomerReferenceChange={(value) => {
                                setCustomerReference(value)
                                markDraftDirty()
                            }}
                            footerText={footerText}
                            onFooterTextChange={(value) => {
                                setFooterText(value)
                                markDraftDirty()
                            }}
                            defaultFooterText={defaultFooterText}
                            onSaveFooterTextAsDefault={saveFooterTextAsDefault}
                            onResetFooterTextToDefault={() => setFooterText(defaultFooterText)}
                            isSavingFooterText={isSavingFooterText}
                        />

                        <RecurringInvoiceCard
                            enabled={isRecurring}
                            cadence={recurringCadence}
                            invoiceDateISO={invoiceDate}
                            onToggle={setIsRecurring}
                            onCadenceChange={setRecurringCadence}
                            disabledReason={recurringDisabledReason()}
                        />

                        <InvoiceActionsBar
                            totals={totals}
                            onSaveAndDownload={saveAndDownload}
                            onSendByEmail={openSendModal}
                            onSaveDraft={saveDraft}
                            onPreview={preview}
                            isCreating={isCreating}
                            isSendingEmail={isSendingEmail}
                            isSavingDraft={isSavingDraft}
                            isPreviewing={isPreviewing}
                            createDisabled={!hasAllRequiredSettings}
                            createDisabledReason="Vul eerst je bedrijfsgegevens aan (zie de melding bovenaan)."
                            draftSavedLabel={draftSavedLabel}
                            onShowPreviewSheet={() => setIsPreviewSheetOpen(true)}
                        />
                    </form>
                </div>

                <div className="sticky top-8 hidden min-w-0 flex-1 xl:block">
                    <div className="rounded-2xl bg-gray-100 p-6 2xl:p-10">
                        {invoicePreview}
                    </div>
                </div>
            </div>

            <Modal
                open={isPreviewSheetOpen}
                updateOpenState={setIsPreviewSheetOpen}
                title="Voorbeeld van je factuur"
                width="4xl"
            >
                <div className="rounded-xl bg-gray-100 p-4 sm:p-6">
                    {invoicePreview}
                </div>
            </Modal>

            <CustomerFormModal
                open={isNewCustomerModalOpen}
                sessionKey={customerModalSession}
                customer={null}
                initialCompanyName={newCustomerInitialName}
                isSaving={isSavingCustomer}
                onClose={() => setIsNewCustomerModalOpen(false)}
                onSave={handleCreateCustomer}
                onArchive={() => {}}
            />

            <SendInvoiceEmailModal
                open={isSendModalOpen}
                kind="send"
                invoiceNumber={sendInvoiceNumber}
                replyToEmail={invoiceContactEmail}
                initialToEmail={selectedCustomer?.email}
                initialCcEmail={selectedCustomer?.ccEmail}
                intro={emailIntro}
                defaultBody={defaultEmailBody}
                attachmentFileName={`${sendInvoiceNumber}.pdf`}
                attachmentAmountLabel={`€ ${decimalToLocalizedString(totals.totalInclVat)}`}
                isSubmitting={isSendingEmail}
                onClose={() => setIsSendModalOpen(false)}
                onSubmit={issueAndSend}
            />
        </Shell>
    )
}
